import React, { useEffect, useState } from 'react';
import {
  collection,
  getDocs,
  doc,
  deleteDoc,
  setDoc,
  query,
  where,
  getDoc,
} from 'firebase/firestore';
import ConfirmationModal from '../../components/SharedFeature/ConfirmationModal';
import { createUserWithEmailAndPassword, getAuth } from 'firebase/auth';
import { initializeApp } from 'firebase/app';
import { firestore } from '../../database/firebase';
import { motion, AnimatePresence } from 'framer-motion';
import {
  HiEye,
  HiTrash,
  HiPencil,
  HiXMark,
  HiUser,
  HiEnvelope,
  HiPhone,
  HiCheckCircle,
  HiExclamationTriangle,
  HiInformationCircle,
  HiPlus,
  HiBriefcase,
  HiIdentification,
  HiUserGroup,
  HiUserCircle,
  HiUserPlus,
  HiCog,
  HiLockClosed,
  HiShieldCheck,
} from 'react-icons/hi2';

const LoadingSpinner = () => (
  <motion.div
    className="flex flex-col items-center justify-center py-20"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
  >
    <motion.div
      className="border-3 h-8 w-8 rounded-full border-blue-200 border-t-blue-600"
      animate={{ rotate: 360 }}
      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
    />
    <p className="mt-4 text-sm text-gray-500">Loading business advisors...</p>
  </motion.div>
);

const Toast = ({ message, type, onClose }) => (
  <motion.div
    initial={{ opacity: 0, y: -50, scale: 0.95 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    exit={{ opacity: 0, y: -50, scale: 0.95 }}
    className={`fixed right-4 top-4 z-50 flex items-center gap-3 rounded-xl px-4 py-3 shadow-lg ${
      type === 'success'
        ? 'border border-green-200 bg-green-50'
        : type === 'error'
          ? 'border border-red-200 bg-red-50'
          : 'border border-gray-200 bg-gray-50'
    }`}
  >
    {type === 'success' && <HiCheckCircle className="h-5 w-5 text-green-600" />}
    {type === 'error' && <HiExclamationTriangle className="h-5 w-5 text-red-600" />}
    {type === 'info' && <HiInformationCircle className="h-5 w-5 text-blue-600" />}
    <span
      className={`text-sm font-medium ${type === 'success' ? 'text-green-800' : type === 'error' ? 'text-red-800' : 'text-black'}`}
    >
      {message}
    </span>
    <button
      onClick={onClose}
      className={`ml-2 hover:opacity-70 ${type === 'success' ? 'text-green-600' : type === 'error' ? 'text-red-600' : 'text-gray-600'}`}
    >
      <HiXMark className="h-4 w-4" />
    </button>
  </motion.div>
);

const initialForm = {
  firstname: '',
  lastname: '',
  company: 'Botswana Digital & Innovation Hub',
  department: '',
  phone: '',
  role: '',
  gender: '',
  prefix: '',
  employeeId: '',
  email: '',
};

const ModeratorsTable = () => {
  const [moderators, setModerators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedModerator, setSelectedModerator] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [toast, setToast] = useState(null);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState(initialForm);
  const [creating, setCreating] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [editModerator, setEditModerator] = useState(null);
  const [editForm, setEditForm] = useState(initialForm);
  const [updating, setUpdating] = useState(false);
  const [clientsForModerator, setClientsForModerator] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState({
    isOpen: false,
    moderator: null,
  });
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchModerators();
  }, []);

  const showToast = (message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchModerators = async () => {
    setLoading(true);
    try {
      const snapshot = await getDocs(collection(firestore, 'moderator'));
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setModerators(data);
      if (data.length === 0) showToast('No business advisors found', 'info');
    } catch (err) {
      setError('Failed to fetch business advisors');
      showToast('Failed to load business advisors', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateModerator = async e => {
    e.preventDefault();
    setCreating(true);
    const defaultPassword = 'Password@12345';
    try {
      const tempApp = initializeApp(
        {
          apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
          authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
          projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
          storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
          messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
          appId: process.env.REACT_APP_FIREBASE_APP_ID,
        },
        'temp-app-' + Date.now()
      );

      const tempAuth = getAuth(tempApp);

      const userCredential = await createUserWithEmailAndPassword(
        tempAuth,
        createForm.email,
        defaultPassword
      );

      const moderatorData = {
        ...createForm,
        uid: userCredential.user.uid,
        id: userCredential.user.uid,
        role: 'moderator',
        status: 'active',
        mustChangePassword: true,
        createdAt: new Date().toISOString(),
        password: defaultPassword, // Store for admin reference
      };

      await setDoc(doc(firestore, 'moderator', userCredential.user.uid), moderatorData);

      await tempAuth.signOut();

      setShowCreateModal(false);
      setGeneratedPassword(defaultPassword);
      setShowPasswordModal(true);
      setCreateForm(initialForm);
      fetchModerators();
      showToast('Business Advisor created successfully!', 'success');
    } catch (error) {
      let errorMessage = 'Failed to create Business Advisor';
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'A user with this email already exists';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Password is too weak';
      } else if (error.message) {
        errorMessage = error.message;
      }

      showToast(errorMessage, 'error');
    } finally {
      setCreating(false);
    }
  };

  const handleEdit = moderator => {
    setEditModerator(moderator);
    setEditForm({ ...moderator });
  };

  const handleUpdateModerator = async e => {
    e.preventDefault();
    setUpdating(true);
    try {
      await setDoc(
        doc(firestore, 'moderator', editModerator.id),
        {
          ...editForm,
          id: editModerator.id,
        },
        { merge: true }
      );
      setEditModerator(null);
      setEditForm(initialForm);
      fetchModerators();
      showToast('Business Advisor updated successfully!', 'success');
    } catch (err) {
      showToast('Failed to update Business Advisor: ' + err.message, 'error');
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteClick = moderator => {
    setDeleteConfirm({
      isOpen: true,
      moderator,
    });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.moderator) return;

    setIsDeleting(true);
    try {
      // Get the full moderator data
      const moderatorDoc = await getDoc(doc(firestore, 'moderator', deleteConfirm.moderator.id));
      const moderatorData = moderatorDoc.data();

      // Add deletion metadata
      const deletedUserData = {
        ...moderatorData,
        originalCollection: 'moderator',
        deletedAt: new Date().toISOString(),
        deletedBy: 'admin',
      };

      // Move to deleted_users collection
      await setDoc(doc(firestore, 'deleted_users', deleteConfirm.moderator.id), deletedUserData);

      // Delete from moderator collection
      await deleteDoc(doc(firestore, 'moderator', deleteConfirm.moderator.id));

      showToast('Business Advisor deleted and moved to deleted users!', 'success');
      setDeleteConfirm({ isOpen: false, moderator: null });
      fetchModerators();
    } catch (err) {
      showToast(`Failed to delete Business Advisor: ${err.message}`, 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm({ isOpen: false, moderator: null });
  };

  const handleView = moderator => {
    setSelectedModerator(moderator);
    setShowModal(true);
  };

  useEffect(() => {
    const fetchClientsForModerator = async () => {
      if (selectedModerator?.email) {
        const q = query(
          collection(firestore, 'clients'),
          where('assignedEmail', '==', selectedModerator.email)
        );
        const snapshot = await getDocs(q);
        setClientsForModerator(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } else {
        setClientsForModerator([]);
      }
    };
    if (showModal && selectedModerator) {
      fetchClientsForModerator();
    }
  }, [selectedModerator, showModal]);

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="mx-auto max-w-7xl">
        <div className="mb-8">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 md:text-3xl">
                Business Advisors Management
              </h1>
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white shadow-sm transition-colors hover:bg-blue-700"
            >
              <HiPlus className="h-5 w-5" />
              Add Business Advisor
            </motion.button>
          </div>
        </div>
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4"
            >
              <div className="flex items-center gap-2">
                <HiExclamationTriangle className="h-5 w-5 text-red-500" />
                <span className="text-red-700">{error}</span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
          {loading ? (
            <div className="p-8 text-center">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent"></div>
              <p className="mt-2 text-gray-600">Loading business advisors...</p>
            </div>
          ) : moderators.length === 0 ? (
            <div className="p-8 text-center">
              <HiUser className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-lg font-medium text-gray-900">No business advisors found</h3>
              <p className="mt-1 text-gray-600">
                Get started by adding your first business advisor.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      <div className="flex items-center gap-2">
                        <HiUser className="h-4 w-4" /> Name
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      <div className="flex items-center gap-2">
                        <HiEnvelope className="h-4 w-4" /> Email
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      <div className="flex items-center gap-2">
                        <HiPhone className="h-4 w-4" /> Phone
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      <div className="flex items-center gap-2">
                        <HiBriefcase className="h-4 w-4" /> Company
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      <div className="flex items-center gap-2">
                        <HiUserGroup className="h-4 w-4" /> Department
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      <div className="flex items-center gap-2">
                        <HiIdentification className="h-4 w-4" /> Employee ID
                      </div>
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {moderators.map((moderator, index) => (
                    <motion.tr
                      key={moderator.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="hover:bg-gray-50"
                    >
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="flex items-center">
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-purple-600 text-white">
                            {(moderator.firstname ||
                              moderator.email ||
                              moderator.id)?.[0]?.toUpperCase() || 'M'}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {moderator.firstname || ''} {moderator.lastname || ''}
                            </div>
                            <div className="text-sm text-gray-500">
                              ID: {moderator.id?.substring(0, 8)}...
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="text-sm text-gray-900">{moderator.email}</div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="text-sm text-gray-900">{moderator.phone}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{moderator.company}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{moderator.department}</div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="text-sm text-gray-900">{moderator.employeeId}</div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm font-medium">
                        <div className="flex items-center gap-2">
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handleView(moderator)}
                            className="group relative rounded-lg bg-gray-100 p-2 text-gray-700 transition-colors hover:bg-gray-200"
                            title="View Details"
                          >
                            <HiEye className="h-4 w-4" />
                            <span className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-black px-2 py-1 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100">
                              View Details
                            </span>
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handleEdit(moderator)}
                            className="group relative rounded-lg bg-blue-100 p-2 text-blue-700 transition-colors hover:bg-blue-200"
                            title="Edit Business Advisor"
                          >
                            <HiPencil className="h-4 w-4" />
                            <span className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-black px-2 py-1 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100">
                              Edit Business Advisor
                            </span>
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handleDeleteClick(moderator)}
                            className="group relative rounded-lg bg-red-100 p-2 text-red-700 transition-colors hover:bg-red-200"
                            title="Delete Business Advisor"
                          >
                            <HiTrash className="h-4 w-4" />
                            <span className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-black px-2 py-1 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100">
                              Delete Business Advisor
                            </span>
                          </motion.button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Advisors</p>
                <p className="text-2xl font-bold text-gray-900">{moderators.length}</p>
              </div>
              <div className="rounded-full bg-blue-100 p-3">
                <HiUser className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active</p>
                <p className="text-2xl font-bold text-gray-900">
                  {moderators.filter(m => m.status === 'Active').length}
                </p>
              </div>
              <div className="rounded-full bg-green-100 p-3">
                <HiCheckCircle className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Departments</p>
                <p className="text-2xl font-bold text-gray-900">
                  {new Set(moderators.map(m => m.department)).size}
                </p>
              </div>
              <div className="rounded-full bg-purple-100 p-3">
                <HiUserGroup className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Companies</p>
                <p className="text-2xl font-bold text-gray-900">
                  {new Set(moderators.map(m => m.company)).size}
                </p>
              </div>
              <div className="rounded-full bg-orange-100 p-3">
                <HiBriefcase className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </div>
        </div>
        <AnimatePresence>
          {showCreateModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
            >
              <motion.div
                initial={{ opacity: 0, y: 50, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 50, scale: 0.95 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className="relative w-full max-w-lg overflow-y-auto rounded-2xl bg-white shadow-2xl"
              >
                <form onSubmit={handleCreateModerator} className="space-y-6 p-6">
                  <h2 className="mb-4 flex items-center gap-2 text-xl font-bold text-gray-900">
                    <HiUserPlus className="h-6 w-6 text-blue-600" /> Create New Business Advisor
                  </h2>

                  <div>
                    <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-blue-900/80">
                      Personal Information
                    </h3>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div className="relative flex items-center">
                        <HiUser className="absolute left-3 h-5 w-5 text-gray-400" />
                        <input
                          type="text"
                          required
                          placeholder="First Name"
                          className="w-full rounded-lg border border-gray-200 px-3 py-2 pl-10 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
                          value={createForm.firstname}
                          onChange={e =>
                            setCreateForm({ ...createForm, firstname: e.target.value })
                          }
                        />
                      </div>
                      <div className="relative flex items-center">
                        <HiUser className="absolute left-3 h-5 w-5 text-gray-400" />
                        <input
                          type="text"
                          required
                          placeholder="Last Name"
                          className="w-full rounded-lg border border-gray-200 px-3 py-2 pl-10 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
                          value={createForm.lastname}
                          onChange={e => setCreateForm({ ...createForm, lastname: e.target.value })}
                        />
                      </div>
                      <div className="relative flex items-center">
                        <HiUserCircle className="absolute left-3 h-5 w-5 text-gray-400" />
                        <select
                          required
                          className="w-full rounded-lg border border-gray-200 px-3 py-2 pl-10 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
                          value={createForm.prefix}
                          onChange={e => setCreateForm({ ...createForm, prefix: e.target.value })}
                        >
                          <option value="">Select Prefix</option>
                          <option value="Mr">Mr</option>
                          <option value="Ms">Ms</option>
                          <option value="Mrs">Mrs</option>
                          <option value="Dr">Dr</option>
                        </select>
                      </div>
                      <div className="relative flex items-center">
                        <HiUserCircle className="absolute left-3 h-5 w-5 text-gray-400" />
                        <select
                          required
                          className="w-full rounded-lg border border-gray-200 px-3 py-2 pl-10 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
                          value={createForm.gender}
                          onChange={e => setCreateForm({ ...createForm, gender: e.target.value })}
                        >
                          <option value="">Select Gender</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-blue-900/80">
                      Contact Information
                    </h3>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div className="relative flex items-center">
                        <HiEnvelope className="absolute left-3 h-5 w-5 text-gray-400" />
                        <input
                          type="email"
                          required
                          placeholder="Email"
                          className="w-full rounded-lg border border-gray-200 px-3 py-2 pl-10 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
                          value={createForm.email}
                          onChange={e => setCreateForm({ ...createForm, email: e.target.value })}
                        />
                      </div>
                      <div className="relative flex items-center">
                        <HiPhone className="absolute left-3 h-5 w-5 text-gray-400" />
                        <input
                          type="text"
                          required
                          placeholder="Phone Number"
                          className="w-full rounded-lg border border-gray-200 px-3 py-2 pl-10 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
                          value={createForm.phone}
                          onChange={e => setCreateForm({ ...createForm, phone: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-blue-900/80">
                      Professional Information
                    </h3>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div className="relative flex items-center">
                        <HiBriefcase className="absolute left-3 h-5 w-5 text-gray-400" />
                        <input
                          type="text"
                          required
                          placeholder="Company"
                          className="w-full rounded-lg border border-gray-200 px-3 py-2 pl-10 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
                          value={createForm.company}
                          onChange={e => setCreateForm({ ...createForm, company: e.target.value })}
                        />
                      </div>
                      <div className="relative flex items-center">
                        <HiUserGroup className="absolute left-3 h-5 w-5 text-gray-400" />
                        <input
                          type="text"
                          required
                          placeholder="Department"
                          className="w-full rounded-lg border border-gray-200 px-3 py-2 pl-10 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
                          value={createForm.department}
                          onChange={e =>
                            setCreateForm({ ...createForm, department: e.target.value })
                          }
                        />
                      </div>
                      <div className="relative flex items-center">
                        <HiIdentification className="absolute left-3 h-5 w-5 text-gray-400" />
                        <input
                          type="text"
                          required
                          placeholder="Employee ID"
                          className="w-full rounded-lg border border-gray-200 px-3 py-2 pl-10 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
                          value={createForm.employeeId}
                          onChange={e =>
                            setCreateForm({ ...createForm, employeeId: e.target.value })
                          }
                        />
                      </div>
                      <div className="relative flex items-center">
                        <HiCog className="absolute left-3 h-5 w-5 text-gray-400" />
                        <input
                          type="text"
                          required
                          placeholder="Role"
                          className="w-full rounded-lg border border-gray-200 px-3 py-2 pl-10 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
                          value={createForm.role}
                          onChange={e => setCreateForm({ ...createForm, role: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg border border-blue-100 bg-blue-50/60 p-4">
                    <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-blue-900/80">
                      Auto-assigned Information
                    </h3>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <HiLockClosed className="h-4 w-4 text-blue-500" />
                        <span>Default Password:</span>
                        <span className="rounded bg-gray-100 px-2 py-1 font-mono text-xs">
                          Password@12345
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <HiShieldCheck className="h-4 w-4 text-blue-500" />
                        <span>Status:</span>
                        <span className="font-medium text-green-600">Active</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <HiUserGroup className="h-4 w-4 text-blue-500" />
                        <span>Role:</span>
                        <span className="font-medium text-blue-600">Moderator</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <HiShieldCheck className="h-4 w-4 text-blue-500" />
                        <span>UID Consistency:</span>
                        <span className="font-medium text-green-600">Firebase Auth UID</span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setShowCreateModal(false)}
                      className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={creating}
                      className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-blue-700 disabled:opacity-60"
                    >
                      {creating ? 'Creating...' : 'Create'}
                    </button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        <AnimatePresence>
          {showPasswordModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
            >
              <motion.div
                initial={{ opacity: 0, y: 50, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 50, scale: 0.95 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className="relative w-full max-w-md overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl"
              >
                <h2 className="mb-4 text-lg font-bold">Business Advisor Created</h2>
                <p className="mb-2">
                  Please share this password with the business advisor. They will be required to
                  change it at first login.
                </p>
                <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-lg text-blue-700">
                  Password@12345
                </div>
                <button
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                  onClick={() => setShowPasswordModal(false)}
                >
                  Done
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        <AnimatePresence>
          {editModerator && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
            >
              <motion.div
                initial={{ opacity: 0, y: 50, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 50, scale: 0.95 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className="relative w-full max-w-lg overflow-y-auto rounded-2xl bg-white shadow-2xl"
              >
                <form onSubmit={handleUpdateModerator} className="space-y-6 p-6">
                  <h2 className="text-xl font-bold text-gray-900">Edit Business Advisor</h2>
                  <div className="grid grid-cols-1 gap-4">
                    <div className="relative flex items-center">
                      <HiUser className="absolute left-3 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        required
                        placeholder="First Name"
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 pl-10 text-sm"
                        value={editForm.firstname}
                        onChange={e => setEditForm({ ...editForm, firstname: e.target.value })}
                      />
                    </div>
                    <div className="relative flex items-center">
                      <HiUser className="absolute left-3 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        required
                        placeholder="Last Name"
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 pl-10 text-sm"
                        value={editForm.lastname}
                        onChange={e => setEditForm({ ...editForm, lastname: e.target.value })}
                      />
                    </div>
                    <div className="relative flex items-center">
                      <HiBriefcase className="absolute left-3 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        required
                        placeholder="Company"
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 pl-10 text-sm"
                        value={editForm.company}
                        onChange={e => setEditForm({ ...editForm, company: e.target.value })}
                      />
                    </div>
                    <div className="relative flex items-center">
                      <HiUserGroup className="absolute left-3 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        required
                        placeholder="Department"
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 pl-10 text-sm"
                        value={editForm.department}
                        onChange={e => setEditForm({ ...editForm, department: e.target.value })}
                      />
                    </div>
                    <div className="relative flex items-center">
                      <HiPhone className="absolute left-3 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        required
                        placeholder="Phone Number"
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 pl-10 text-sm"
                        value={editForm.phone}
                        onChange={e => setEditForm({ ...editForm, phone: e.target.value })}
                      />
                    </div>
                    <div className="relative flex items-center">
                      <HiCog className="absolute left-3 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        required
                        placeholder="Role"
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 pl-10 text-sm"
                        value={editForm.role}
                        onChange={e => setEditForm({ ...editForm, role: e.target.value })}
                      />
                    </div>
                    <div className="relative flex items-center">
                      <HiUserCircle className="absolute left-3 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        required
                        placeholder="Gender"
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 pl-10 text-sm"
                        value={editForm.gender}
                        onChange={e => setEditForm({ ...editForm, gender: e.target.value })}
                      />
                    </div>
                    <div className="relative flex items-center">
                      <HiUserPlus className="absolute left-3 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        required
                        placeholder="Prefix"
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 pl-10 text-sm"
                        value={editForm.prefix}
                        onChange={e => setEditForm({ ...editForm, prefix: e.target.value })}
                      />
                    </div>
                    <div className="relative flex items-center">
                      <HiIdentification className="absolute left-3 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        required
                        placeholder="Employee ID"
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 pl-10 text-sm"
                        value={editForm.employeeId}
                        onChange={e => setEditForm({ ...editForm, employeeId: e.target.value })}
                      />
                    </div>
                    <div className="relative flex items-center">
                      <HiEnvelope className="absolute left-3 h-5 w-5 text-gray-400" />
                      <input
                        type="email"
                        required
                        placeholder="Email"
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 pl-10 text-sm"
                        value={editForm.email}
                        onChange={e => setEditForm({ ...editForm, email: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setEditModerator(null)}
                      className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={updating}
                      className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-blue-700 disabled:opacity-60"
                    >
                      {updating ? 'Updating...' : 'Update'}
                    </button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        <AnimatePresence>
          {showModal && selectedModerator && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
            >
              <motion.div
                initial={{ opacity: 0, y: 50, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 50, scale: 0.95 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-gray-100 bg-gradient-to-br from-white to-blue-50 shadow-2xl md:max-w-2xl"
              >
                <div className="flex flex-col gap-4 rounded-t-2xl border-b border-gray-100 bg-white/80 px-6 py-4 md:flex-row md:items-center md:justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-green-600 text-lg font-bold text-white shadow-sm">
                      {(selectedModerator.firstname ||
                        selectedModerator.email ||
                        selectedModerator.id)?.[0]?.toUpperCase() || 'M'}
                    </div>
                    <div>
                      <div className="flex flex-col gap-0.5">
                        <span className="text-base font-semibold text-gray-900">
                          {selectedModerator.firstname || ''} {selectedModerator.lastname || ''}
                        </span>
                        <span className="text-xs text-gray-500">{selectedModerator.email}</span>
                      </div>
                    </div>
                  </div>
                  <button
                    className="self-start rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 md:self-auto"
                    onClick={() => setShowModal(false)}
                    aria-label="Close"
                  >
                    <HiXMark className="h-6 w-6" />
                  </button>
                </div>
                <div className="space-y-8 p-6">
                  {/* Moderator Info Section */}
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <div className="flex flex-col gap-3">
                      <div className="flex items-center gap-2 text-sm font-semibold text-blue-900/80">
                        <HiEnvelope className="h-4 w-4 text-blue-600" /> Email
                      </div>
                      <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                        {selectedModerator.email}
                      </div>
                      <div className="mt-3 flex items-center gap-2 text-sm font-semibold text-blue-900/80">
                        <HiPhone className="h-4 w-4 text-blue-600" /> Phone
                      </div>
                      <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                        {selectedModerator.phone}
                      </div>
                    </div>
                    <div className="flex flex-col gap-3">
                      <div className="flex items-center gap-2 text-sm font-semibold text-blue-900/80">
                        <HiBriefcase className="h-4 w-4 text-blue-600" /> Company
                      </div>
                      <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                        {selectedModerator.company}
                      </div>
                      <div className="mt-3 flex items-center gap-2 text-sm font-semibold text-blue-900/80">
                        <HiUserGroup className="h-4 w-4 text-blue-600" /> Department
                      </div>
                      <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                        {selectedModerator.department}
                      </div>
                      <div className="mt-3 flex items-center gap-2 text-sm font-semibold text-blue-900/80">
                        <HiIdentification className="h-4 w-4 text-blue-600" /> Employee ID
                      </div>
                      <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                        {selectedModerator.employeeId}
                      </div>
                    </div>
                  </div>
                  {/* Assigned Clients Section */}
                  <div className="">
                    <div className="mb-2 flex items-center gap-2 text-base font-semibold text-green-900/90">
                      <HiUser className="h-5 w-5 text-green-600" /> Assigned Clients
                    </div>
                    {clientsForModerator.length === 0 ? (
                      <div className="rounded border border-gray-100 bg-gray-50 px-3 py-2 text-sm text-gray-500">
                        No clients assigned to this advisor.
                      </div>
                    ) : (
                      <ul className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        {clientsForModerator.map(client => (
                          <li
                            key={client.id}
                            className="flex flex-col gap-1 rounded-lg border border-gray-100 bg-white px-3 py-2 shadow-sm"
                          >
                            <div className="flex items-center gap-2">
                              <HiUser className="h-4 w-4 text-blue-500" />
                              <span className="font-medium text-black">
                                {client.firstname} {client.lastname}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-gray-600">
                              <HiEnvelope className="h-4 w-4" /> {client.email}
                            </div>
                            <div className="flex items-center gap-2 text-xs text-gray-600">
                              <HiBriefcase className="h-4 w-4" /> {client.organization || '-'}
                            </div>
                            <div className="flex items-center gap-2 text-xs text-gray-600">
                              <HiCheckCircle className="h-4 w-4" /> {client.status || '-'}
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        <AnimatePresence>
          {toast && (
            <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
          )}
        </AnimatePresence>

        <ConfirmationModal
          isOpen={deleteConfirm.isOpen}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          title="Delete Business Advisor"
          message={`Are you sure you want to delete ${deleteConfirm.moderator?.firstname || ''} ${deleteConfirm.moderator?.lastname || ''}? They will be moved to deleted users.`}
          confirmText="Delete"
          cancelText="Cancel"
          confirmButtonColor="bg-red-600 hover:bg-red-700"
          isLoading={isDeleting}
        />
      </div>
    </div>
  );
};

export default ModeratorsTable;
